import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import Ariane, { TYPE_PUBLIC } from 'components/Ariane/Ariane';
import PageNotFound from 'components/Common/Errors/PageNotFound/PageNotFound';
import Loader from 'components/Common/Loader/Loader';
import Loading from 'components/Common/Loading/Loading';
import { ScrollToTop } from 'components/Common/ScrollToTop/ScrollToTop';
import SearchProducts from 'components/Listing/SearchProducts/SearchProducts';
import SearchWithoutResult from 'components/Listing/SearchWithoutResult/SearchWithoutResult';
import useSearchProductListing from 'hooks/search/useSearchProductListing';
import useScrollTop from 'hooks/useScrollTop';
import { SearchListingWithtResultType } from 'types/ProductListing/SearchProductListing';

import styles from './page.module.scss';

export default function SearchPage(): JSX.Element {
  const { t } = useTranslation(['translation', 'listingPage']);
  const { data, error, isValidating } = useSearchProductListing();
  const title = t('META_TITLE_TEMPLATE', {
    title: t('SEARCH_PAGE_META_TITLE'),
  });

  useScrollTop();

  if (error) {
    return (
      <>
        <ScrollToTop />
        <PageNotFound status={'500'} />
      </>
    );
  }

  if (!data) {
    return <Loader />;
  }

  const { productCount, searchKeyword } = data;

  return (
    <div className={styles.container}>
      <RenderHelmet title={title} />
      <Ariane
        items={[
          {
            label: t('SEARCH_RESULTS_FOR', {
              text: searchKeyword,
              ns: 'listingPage',
            }),
          },
        ]}
        type={TYPE_PUBLIC}
        classes={{ nav: styles.nav }}
      />
      {productCount > 0 ? (
        <SearchProducts
          title={title}
          data={data as SearchListingWithtResultType}
          isDataValidating={isValidating ?? false}
        />
      ) : (
        <SearchWithoutResult
          searchKeyword={searchKeyword}
          totalProductCount={0}
          title={title}
        />
      )}
      {isValidating && <Loading loading />}
    </div>
  );
}

function RenderHelmet({ title }: { title: string }): JSX.Element {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}
